import React from 'react';
import {
  EnumCellProps,
  isBooleanControl,
  JsonFormsCellRendererRegistryEntry,
  RankedTester,
  rankWith,
} from '@jsonforms/core';
import { TranslateProps, withJsonFormsEnumCellProps, withTranslateProps } from '@jsonforms/react';
import { RENDERER_RANK_PREFERRED_MATCH } from '../constants';
import { EnumCellRenderer } from './EnumCell';

/**
 * Tester to determine if a control can be used with this cell.
 */
const tester: RankedTester = rankWith(RENDERER_RANK_PREFERRED_MATCH, isBooleanControl);

/**
 * Base cell renderer to show a single-select enum input control.
 * @param props Control props to be hydrated by context.
 */
const BooleanCellRenderer = (props: EnumCellProps & TranslateProps) => {
  // If options are already provided (e.g. via enum in schema), reuse them; otherwise supply true/false.
  const booleanLabelParams = {
    schema: props.schema,
    uischema: props.uischema,
    path: props.path,
  };
  const options =
    props.options && props.options.length > 0
      ? props.options
      : [
          {
            value: true,
            label: props.t(
              `${props.uischema.i18n ?? props.path}.true`,
              props.t('boolean.true', 'True', booleanLabelParams),
              booleanLabelParams
            ),
          },
          {
            value: false,
            label: props.t(
              `${props.uischema.i18n ?? props.path}.false`,
              props.t('boolean.false', 'False', booleanLabelParams),
              booleanLabelParams
            ),
          },
        ];
  // Delegate rendering to EnumCellRenderer to avoid duplication.
  return (
    <EnumCellRenderer
      {...props}
      handleChange={(path, newValue) =>
        props.handleChange(
          path,
          // can come in as string or boolean depending on platform
          newValue === true || newValue === 'true'
            ? true
            : newValue === false || newValue === 'false'
            ? false
            : undefined
        )
      }
      options={options}
    />
  );
};

/**
 * Renderer registry entry for input control.
 * Bundles the tester and the wrapped control for use with form.
 */
export const BooleanCell: JsonFormsCellRendererRegistryEntry = {
  tester,
  cell: withJsonFormsEnumCellProps(withTranslateProps(BooleanCellRenderer)),
};
